/**
 * Coupon Performance Monitor
 * Tracks timing for discount calculations and API calls
 */
import { logger } from '@/utils/log';

/**
 * Performance thresholds (in milliseconds)
 */
const THRESHOLDS = {
  DISCOUNT_CALCULATION: 50, // Warn if discount calc takes >50ms
  API_CALL: 3000, // Warn if API call takes >3s
  STATE_UPDATE: 20, // Warn if state update takes >20ms
};

/**
 * Performance monitor singleton for coupon operations
 */
class CouponPerformanceMonitor {
  constructor() {
    /**
     * Active timing marks
     * @type {Map<string, number>}
     */
    this.marks = new Map();

    /**
     * Completed measurements
     * @type {Array<{name: string, duration: number, timestamp: number}>}
     */
    this.measurements = [];

    /**
     * Maximum measurements to keep in memory
     */
    this.maxMeasurements = 100;

    /**
     * Whether monitoring is enabled
     */
    this.enabled = process.env.NODE_ENV === 'development';
  }

  /**
   * Start a timing mark
   * @param {string} name - Mark name
   */
  start(name) {
    if (!this.enabled) return;
    this.marks.set(name, performance.now());
  }

  /**
   * End a timing mark and record the measurement
   * @param {string} name - Mark name
   * @param {Object} options - Options
   * @param {number} options.threshold - Custom threshold for warning
   * @param {boolean} options.silent - Don't log warning even if exceeded
   * @returns {number} Duration in milliseconds
   */
  end(name, options = {}) {
    if (!this.enabled) return 0;

    const startTime = this.marks.get(name);
    if (startTime === undefined) {
      logger.warn(`[CouponPerf] No start mark found for: ${name}`);
      return 0;
    }

    const endTime = performance.now();
    const duration = endTime - startTime;

    // Store measurement
    this.addMeasurement(name, duration);

    // Clean up mark
    this.marks.delete(name);

    // Check threshold and log warning if exceeded
    const threshold = options.threshold || this.getDefaultThreshold(name);
    if (!options.silent && duration > threshold) {
      logger.warn(
        `[CouponPerf] ${name} took ${duration.toFixed(2)}ms (threshold: ${threshold}ms)`
      );
    }

    return duration;
  }

  /**
   * Measure async operation
   * @param {string} name - Operation name
   * @param {Function} fn - Async function to measure
   * @param {Object} options - Options
   * @returns {Promise<*>} Result of the function
   */
  async measureAsync(name, fn, options = {}) {
    if (!this.enabled) return fn();

    this.start(name);
    try {
      const result = await fn();
      this.end(name, options);
      return result;
    } catch (error) {
      this.end(name, { ...options, silent: true });
      throw error;
    }
  }

  /**
   * Measure sync operation
   * @param {string} name - Operation name
   * @param {Function} fn - Sync function to measure
   * @param {Object} options - Options
   * @returns {*} Result of the function
   */
  measure(name, fn, options = {}) {
    if (!this.enabled) return fn();

    this.start(name);
    try {
      const result = fn();
      this.end(name, options);
      return result;
    } catch (error) {
      this.end(name, { ...options, silent: true });
      throw error;
    }
  }

  /**
   * Add measurement to history
   * @param {string} name - Measurement name
   * @param {number} duration - Duration in ms
   */
  addMeasurement(name, duration) {
    this.measurements.push({
      name,
      duration,
      timestamp: Date.now(),
    });

    // Keep only recent measurements
    if (this.measurements.length > this.maxMeasurements) {
      this.measurements.shift();
    }
  }

  /**
   * Get default threshold for a measurement type
   * @param {string} name - Measurement name
   * @returns {number} Threshold in ms
   */
  getDefaultThreshold(name) {
    if (name.includes('api') || name.includes('Api')) {
      return THRESHOLDS.API_CALL;
    }
    if (name.includes('state') || name.includes('State')) {
      return THRESHOLDS.STATE_UPDATE;
    }
    return THRESHOLDS.DISCOUNT_CALCULATION;
  }

  /**
   * Get statistics for a measurement type
   * @param {string} name - Measurement name (partial match)
   * @returns {Object} Statistics
   */
  getStats(name) {
    const filtered = this.measurements.filter((m) =>
      name ? m.name.includes(name) : true
    );

    if (filtered.length === 0) {
      return { count: 0, avg: 0, min: 0, max: 0, total: 0 };
    }

    const durations = filtered.map((m) => m.duration);
    const total = durations.reduce((a, b) => a + b, 0);

    return {
      count: filtered.length,
      avg: total / filtered.length,
      min: Math.min(...durations),
      max: Math.max(...durations),
      total,
    };
  }

  /**
   * Get all recent measurements
   * @returns {Array} Measurements
   */
  getMeasurements() {
    return [...this.measurements];
  }

  /**
   * Clear all measurements
   */
  clear() {
    this.marks.clear();
    this.measurements = [];
  }

  /**
   * Log performance summary to console
   */
  logSummary() {
    if (!this.enabled) return;

    const discountStats = this.getStats('discount');
    const apiStats = this.getStats('api');

    logger.info('[CouponPerf] Performance Summary:');
    logger.info(`  Discount calculations: ${discountStats.count} calls, avg ${discountStats.avg.toFixed(2)}ms`);
    logger.info(`  API calls: ${apiStats.count} calls, avg ${apiStats.avg.toFixed(2)}ms`);
  }

  /**
   * Enable monitoring
   */
  enable() {
    this.enabled = true;
  }

  /**
   * Disable monitoring
   */
  disable() {
    this.enabled = false;
  }
}

// Export singleton instance
export const couponPerformanceMonitor = new CouponPerformanceMonitor();

// Export class for testing
export { CouponPerformanceMonitor };

// ============================================================================
// Convenience functions for common measurements
// ============================================================================

/**
 * Measure discount calculation
 * @param {Function} fn - Calculation function
 * @returns {*} Result of calculation
 */
export function measureDiscountCalculation(fn) {
  return couponPerformanceMonitor.measure('discountCalculation', fn, {
    threshold: THRESHOLDS.DISCOUNT_CALCULATION,
  });
}

/**
 * Measure API call
 * @param {string} apiName - API name
 * @param {Function} fn - Async API function
 * @returns {Promise<*>} API result
 */
export function measureApiCall(apiName, fn) {
  return couponPerformanceMonitor.measureAsync(`api_${apiName}`, fn, {
    threshold: THRESHOLDS.API_CALL,
  });
}

/**
 * Measure state update
 * @param {Function} fn - State update function
 * @returns {*} Result
 */
export function measureStateUpdate(fn) {
  return couponPerformanceMonitor.measure('stateUpdate', fn, {
    threshold: THRESHOLDS.STATE_UPDATE,
  });
}
